/**
 * Strukturált adatok kinyerése a mappa gerincszövegéből (C3).
 *
 * ═══ MIÉRT NEM SÉRTI EZ A 13. ALAPELVET ═══
 *
 * A 13. alapelv szerint a felismerés (OCR/AI) SOSEM írhatja a
 * `currentText`-et. Ez a parser nem a nyers felismerésből dolgozik, hanem a
 * MÁR EMBER ÁLTAL JÓVÁHAGYOTT `currentText`-ből — a származtatott érték
 * tehát ugyanazt a tekintélyt örökli, mint a forrása. A nyers felismerés
 * továbbra sem ér el a `folder` tábláig.
 *
 * Következmény: a mezők minden szövegíráskor ÚJRASZÁMOLÓDNAK. Nincs külön
 * „elemzés" gomb, és nem tudnak elcsúszni a szövegtől.
 *
 * ═══ MIÉRT DETERMINISZTIKUS PARSER ÉS NEM AI ═══
 *
 * A 204 valós irattári fényképből kiderült, hogy a nyomtatott gerinckártyák
 * következetes mezősorrendet követnek:
 *
 *   PERSPED / KFT → BEÉRKEZETT SZÁMLÁK → FORINT → 5284-5348 → 1. → 2011
 *   PERSPED / KFT → PÉNZTÁR → 01.03-01.13 → 1. → 2011
 *   GREENWORK KFT → LELTÁR → II. → 462-48 → 2017
 *
 * Ez egységtesztelhető, ingyenes és kiszámítható. Az AI a rendhagyó és
 * kézírásos gerincekhez marad tartaléknak (a kék mappák gerincén kézírás
 * van) — de az AI eredménye JAVASLAT, amit ember fogad el.
 */
import { DocumentType, SpineCurrency, UNCLASSIFIED_COMPANY } from './enums.js';
/** Üres eredmény — a `company` itt is kötelezően kitöltött. */
export const EMPTY_SPINE_FIELDS = {
    company: UNCLASSIFIED_COMPANY,
    documentType: null,
    currency: null,
    yearFrom: null,
    yearTo: null,
    periodFrom: null,
    periodTo: null,
    numberFrom: null,
    numberTo: null,
    volume: null,
};
/**
 * A cégek KEZDŐ készlete — a `company` és `company_alias` táblák seedje (F1).
 *
 * ⚠ EZ NEM A FUTÁSIDEJŰ FORRÁS. A felismerés a `parseSpineText` második
 * paraméteréből dolgozik, amit az API az ADATBÁZISBÓL tölt — különben egy új
 * cég felvétele megint kódváltozás és kitelepítés lenne, ami épp az F1
 * kiváltó oka volt. Ez a lista csak azt rögzíti, mivel indul egy üres
 * adatbázis, és mit vár a parser arany-teszthalmaza.
 */
export const SEED_COMPANIES = [
    { code: 'PERSPED', name: 'Persped', aliases: ['persped', 'perspd'] },
    { code: 'GREENMAN', name: 'Greenman', aliases: ['greenman', 'green man'] },
    { code: 'GREENWORK', name: 'Greenwork', aliases: ['greenwork', 'green work'] },
    { code: 'WESCAN', name: 'Wescan', aliases: ['wescan', 'vescan'] },
    { code: 'BAULOGISTIC', name: 'BauLogistic', aliases: ['baulogistic', 'bau logistic'] },
    { code: 'PALLETWAYS', name: 'Palletways', aliases: ['palletways'] },
    { code: 'PLASTICON', name: 'Plasticon', aliases: ['plasticon'] },
    { code: 'GREEN_BIOTIC', name: 'Green Biotic', aliases: ['green biotic', 'greenbiotic'] },
    /**
     * A besorolatlan SOSEM tűnhet el és sosem deaktiválható: ez a
     * `folder.spine_company` oszlop DEFAULT-ja. A migráció CHECK-kel is
     * kikényszeríti (`company_unclassified_always_active_ck`).
     */
    { code: UNCLASSIFIED_COMPANY, name: 'Besorolatlan', aliases: [] },
];
/** A `SEED_COMPANIES` lapítva, ahogy a parser várja. */
export const SEED_COMPANY_ALIASES = SEED_COMPANIES.flatMap((c) => c.aliases.map((alias) => ({ alias, companyCode: c.code })));
/**
 * Dokumentumtípus-változatok → enum.
 *
 * A HOSSZABB minták előrébb: a "beérkezett számlák" előbb illeszkedjen, mint
 * a puszta "számlák", különben az utóbbi elnyelné.
 */
const DOCUMENT_TYPE_ALIASES = [
    ['beerkezett szamlak', DocumentType.BEERKEZETT_SZAMLAK],
    ['bejovo szamlak', DocumentType.BEERKEZETT_SZAMLAK],
    ['kimeno szamlak', DocumentType.KIMENO_SZAMLAK],
    ['kimeno fuvar', DocumentType.KIMENO_SZAMLAK],
    ['targyi eszkoz kartonok', DocumentType.TARGYI_ESZKOZ_KARTONOK],
    ['targyi eszkoz', DocumentType.TARGYI_ESZKOZ_KARTONOK],
    ['fokonyvi kartonok', DocumentType.FOKONYVI_KARTONOK],
    ['fokonyvi karton', DocumentType.FOKONYVI_KARTONOK],
    ['bevallasok', DocumentType.BEVALLASOK],
    ['bevallas', DocumentType.BEVALLASOK],
    ['penztar', DocumentType.PENZTAR],
    ['leltar', DocumentType.LELTAR],
    ['osrevizio', DocumentType.OSREVIZIO],
    ['forrasok', DocumentType.FORRASOK],
];
const CURRENCY_ALIASES = [
    ['forint', SpineCurrency.HUF],
    ['huf', SpineCurrency.HUF],
    ['eur', SpineCurrency.EUR],
    ['euro', SpineCurrency.EUR],
];
/** Magyar hónapnevek → sorszám. A ragozott alakokat a prefix-illesztés fogja. */
const MONTH_NAMES = [
    ['januar', 1],
    ['februar', 2],
    ['marcius', 3],
    ['aprilis', 4],
    ['majus', 5],
    ['junius', 6],
    ['julius', 7],
    ['augusztus', 8],
    ['szeptember', 9],
    ['oktober', 10],
    ['november', 11],
    ['december', 12],
];
/** Római számok kötetszámhoz — a gerinceken legfeljebb néhányig mennek. */
const ROMAN_NUMERALS = [
    ['viii', 8],
    ['vii', 7],
    ['vi', 6],
    ['iv', 4],
    ['ix', 9],
    ['iii', 3],
    ['ii', 2],
    ['v', 5],
    ['x', 10],
    ['i', 1],
];
/**
 * A gerincen a rendszer SAJÁT vonalkódja is szerepelhet — sőt, szándékosan
 * rajta van a fotón, hogy a címke ↔ gerinc párosítás visszakereshető legyen.
 * A gerincSZÖVEGBE viszont nem való: kiszűrjük, mielőtt bármit értelmeznénk,
 * különben az „5 számjegy" mintája évnek vagy tartománynak látszana.
 */
export const OWN_BARCODE_PATTERN = /\bMAP-\d{5}\b/g;
/**
 * A MI SAJÁT címkefeliratunk, amit az AI szintén beolvas a fotóról (G3,
 * 2026-08-14-i CR #3).
 *
 * A mappára ragasztott címkén ott a rendszernév („Persped Irattár - Mappa"),
 * és enélkül a cégillesztés MINDEN mappát Perspedként ismerne fel.
 *
 * ⚠ A minták a TELJES feliratra illeszkednek, sosem a „persped" szóra: a
 * Persped valódi cég a gerinceken, a szó kiszűrése pont a leggyakoribb
 * besorolást rontaná el. Erre teszt van (`spine.test.ts`).
 *
 * ÉLES ÚTON a DB-listát kell átadni (`spine_ignore_pattern` tábla) — ez itt
 * csak a tesztek és a fejlesztői kényelem kedvéért van, a
 * `SEED_COMPANY_ALIASES` mintájára.
 */
export const SEED_SPINE_IGNORE_PATTERNS = [
    'persped irattar - mappa',
    'persped irattar - raklap',
    'persped irattar - polc',
    'persped irattar - tarolo',
    'persped irattar',
];
/**
 * A kihagyandó minták kivonása a NORMALIZÁLT szövegből.
 *
 * A LEGHOSSZABB mintától indulunk: ha a „persped irattar" előbb futna le,
 * a „persped irattar - mappa"-ból egy árva „- mappa" maradna, ami zajt visz
 * a további mezőillesztésbe. A hosszabb minta mindig specifikusabb.
 *
 * A találat helyére SZÓKÖZ kerül, nem üres string: két összeragadó szó
 * (`...kft` + `leltar...`) különben új, hamis szót képezne.
 */
function stripIgnored(haystack, patterns) {
    let result = haystack;
    for (const pattern of [...patterns].sort((a, b) => b.length - a.length)) {
        if (!pattern)
            continue;
        result = result.split(pattern).join(' ');
    }
    return result;
}
/** Ékezetek és kis/nagybetű normalizálása az illesztéshez. */
export function normalize(text) {
    return text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();
}
/**
 * A cég illesztése a DB-ből jövő aliasokra (F1).
 *
 * A LEGHOSSZABB illeszkedő alias nyer, nem az első. A hardkódolt listánál a
 * sorrend a fájlban látszott és karbantartható volt; adatbázissorokból a
 * beszúrási sorrend esetleges, és egy később felvett rövid alias (`green`)
 * kiüthetné a meglévő pontosat (`greenman`). A hossz szerinti rendezés ezt
 * szerkezetileg zárja ki — a specifikusabb minta mindig előrébb kerül.
 */
function matchCompany(haystack, aliases) {
    let best = null;
    for (const candidate of aliases) {
        if (!haystack.includes(candidate.alias))
            continue;
        if (best === null || candidate.alias.length > best.alias.length)
            best = candidate;
    }
    return best?.companyCode ?? null;
}
/** Az első illeszkedő alias értéke, a lista sorrendjében. */
function matchAlias(haystack, aliases) {
    for (const [needle, value] of aliases) {
        if (haystack.includes(needle))
            return value;
    }
    return null;
}
function parseYears(text) {
    // Évtartomány (2016-2017) ELŐBB, mint a magányos év — különben a magányos
    // minta elkapná a tartomány első felét, és a másodikat eldobná.
    const range = text.match(/\b(19\d{2}|20\d{2})\s*[-–]\s*(19\d{2}|20\d{2})\b/);
    if (range) {
        // A kisebbik az alsó határ. Egy megfordult tartomány (`2020-2016`) a
        // gerincen OCR-hiba vagy elgépelés — a `folder_spine_year_range_ck`
        // viszont elutasítaná, és ezzel a REGISZTRÁCIÓT buktatná meg. A két
        // szám így megmarad, csak a helyes sorrendben.
        const a = Number(range[1]);
        const b = Number(range[2]);
        return { yearFrom: Math.min(a, b), yearTo: Math.max(a, b) };
    }
    const single = text.match(/\b(19\d{2}|20\d{2})\b/);
    if (single) {
        const year = Number(single[1]);
        return { yearFrom: year, yearTo: year };
    }
    return { yearFrom: null, yearTo: null };
}
function pad2(value) {
    return String(value).padStart(2, '0');
}
/**
 * Időszak kinyerése. Három alak fordul elő a valós gerinceken:
 *
 *   `01.03-01.13`  → dátumtartomány (MM.DD-MM.DD)  → "01-03" .. "01-13"
 *   `07-09. hó`    → hónaptartomány                → "07" .. "09"
 *   `JANUÁR-MÁJUS` → hónapnevek                    → "01" .. "05"
 *
 * ⚠ A dátumtartomány mintája SOSEM haraphat bele egy hosszabb számba. A `\b`
 * erre nem elég: a `1101/19-1200/19` számlatartományban a `/` után szóhatár
 * van, így a régi minta a `19- 1200` részletből „1", „9", „12", „00" mezőket
 * olvasott ki — `01-09` .. `12-00`. A `00` nap nem létezik, az adatbázis
 * `folder_spine_period_format_ck` ellenőrzése elutasította, és ezzel a mappa
 * REGISZTRÁCIÓJA bukott el 500-zal (2026-08/09, 37 mappa a TC22 Tálcáján).
 * Ezért:
 *
 *   - a hónap és a nap között KÖTELEZŐ az elválasztó (`[.\s]+`), így a minta
 *     nem tud egy számjegysorozat közepén kezdeni;
 *   - `(?<!\d)` / `(?!\d)` zárja ki a hosszabb számba lógást;
 *   - a nap alsó határa 1, nem 0 — a puszta felső határ átengedte a nulladik
 *     napot, amit aztán csak az adatbázis fogott meg.
 */
function parsePeriod(text) {
    const dateRange = text.match(/(?<!\d)(\d{1,2})[.\s]+(\d{1,2})\s*[-–]\s*(\d{1,2})[.\s]+(\d{1,2})(?!\d)/);
    if (dateRange) {
        const [, m1, d1, m2, d2] = dateRange.map(Number);
        const honapOk = m1 >= 1 && m1 <= 12 && m2 >= 1 && m2 <= 12;
        const napOk = d1 >= 1 && d1 <= 31 && d2 >= 1 && d2 <= 31;
        if (honapOk && napOk) {
            return { periodFrom: `${pad2(m1)}-${pad2(d1)}`, periodTo: `${pad2(m2)}-${pad2(d2)}` };
        }
    }
    const monthRange = text.match(/\b(\d{1,2})\s*[-–]\s*(\d{1,2})\.?\s*ho\b/);
    if (monthRange) {
        const from = Number(monthRange[1]);
        const to = Number(monthRange[2]);
        if (from >= 1 && from <= 12 && to >= 1 && to <= 12) {
            return { periodFrom: pad2(from), periodTo: pad2(to) };
        }
    }
    const singleMonth = text.match(/\b(\d{1,2})\.?\s*ho\b/);
    if (singleMonth) {
        const month = Number(singleMonth[1]);
        if (month >= 1 && month <= 12)
            return { periodFrom: pad2(month), periodTo: pad2(month) };
    }
    // Hónapnevek: prefix-illesztés, hogy a ragozott alak ("májustól") is fogjon.
    const found = MONTH_NAMES.filter(([name]) => text.includes(name.slice(0, 5))).map(([, n]) => n);
    if (found.length >= 2) {
        return { periodFrom: pad2(Math.min(...found)), periodTo: pad2(Math.max(...found)) };
    }
    if (found.length === 1) {
        return { periodFrom: pad2(found[0]), periodTo: pad2(found[0]) };
    }
    return { periodFrom: null, periodTo: null };
}
/**
 * Irat-/számlatartomány (`5284-5348`, `1-190`, `1101/19-1200/19`).
 *
 * Az ÉVEKET ki kell zárni, különben a `2016-2017` évtartomány
 * számtartománynak is látszana — ezért a négyjegyű, 19xx/20xx alakú párokat
 * itt nem fogadjuk el.
 *
 * A `szám/év` alak (`1101/19-1200/19`, „Nemzetközi fuvarmegbízások") saját
 * mintát kap, és ELŐBB fut, mint az általános. Enélkül az általános minta a
 * `/` utáni szóhatárnál kezdene, és `19`–`1200`-at adna a helyes
 * `1101`–`1200` helyett — némán rontva a keresést. Ugyanez a szóhatár-csapda
 * buktatta el élesben a `parsePeriod`-ot is.
 */
function parseNumberRange(text) {
    const perjeles = text.match(/(?<!\d)(\d{1,6})\/\d{2,4}\s*[-–]\s*(\d{1,6})\/\d{2,4}(?!\d)/);
    if (perjeles)
        return { numberFrom: perjeles[1], numberTo: perjeles[2] };
    const matches = [...text.matchAll(/(?<![\d/])(\d{1,6})\s*[-–]\s*(\d{1,6})(?![\d/])/g)];
    for (const m of matches) {
        const from = m[1];
        const to = m[2];
        const looksLikeYears = /^(19|20)\d{2}$/.test(from) && /^(19|20)\d{2}$/.test(to);
        if (looksLikeYears)
            continue;
        return { numberFrom: from, numberTo: to };
    }
    return { numberFrom: null, numberTo: null };
}
/**
 * Kötetszám: `1.` vagy római `II.`. Az évszámokat nem tekintjük kötetnek.
 *
 * ⚠ A NULLADIK KÖTET NEM LÉTEZIK. A `folder_spine_volume_positive_ck`
 * elutasítja, és ezzel a mappa regisztrációját buktatja meg — élesben a
 * `loo 0.` OCR-szövegen (MAP-03194, 2026-09-01) pontosan ez történt.
 */
function parseVolume(text) {
    const roman = text.match(/\b([ivx]{1,4})\.\s/);
    if (roman) {
        const value = matchAlias(roman[1], ROMAN_NUMERALS.map(([r, n]) => [r, n]));
        if (value !== null && roman[1] === ROMAN_NUMERALS.find(([, n]) => n === value)?.[0]) {
            return value;
        }
    }
    // A `0*[1-9]` alak zárja ki a nullát: a `loo 0.` így egyszerűen nem
    // illeszkedik, és a minta megy tovább a szövegben egy VALÓDI kötetszámért.
    const arabic = text.match(/(?:^|\s)(0*[1-9]\d?)\.(?:\s|$)/);
    if (arabic)
        return Number(arabic[1]);
    return null;
}
/**
 * A gerincszöveg strukturált mezőkre bontása.
 *
 * Sosem dob és sosem ad `null` objektumot: felismerhetetlen szövegre az
 * `EMPTY_SPINE_FIELDS` jön vissza (`company: BESOROLATLAN`). Ez teszi
 * biztonságossá, hogy minden szövegíráskor lefuthasson.
 *
 * A `companyAliases` az ADATBÁZISBÓL jön (F1) — a `shared` csomag így tiszta
 * marad, az API tölti be és adja át. Elhagyva a `SEED_COMPANY_ALIASES`-szel
 * dolgozik: ez a tesztek és a fejlesztői kényelem kedvéért van, ÉLES ÚTON
 * mindig a DB-listát kell átadni, különben egy frissen felvett cég némán
 * kimaradna a felismerésből.
 *
 * Az `ignorePatterns` ugyanígy a DB-ből jön (G3): ezek a MI SAJÁT
 * címkefeliratunk normalizált alakjai, amiket a fotóról az AI szintén
 * beolvas. Enélkül a „Persped Irattár - Mappa" felirat minden mappát
 * Perspedként osztályozna.
 */
export function parseSpineText(text, companyAliases = SEED_COMPANY_ALIASES, ignorePatterns = SEED_SPINE_IGNORE_PATTERNS) {
    if (!text || !text.trim())
        return { ...EMPTY_SPINE_FIELDS };
    return szurtEredmeny(elemez(text, companyAliases, ignorePatterns));
}
/**
 * Az `MM` / `MM-DD` alak — a `folder_spine_period_format_ck` adatbázis-
 * ellenőrzés PONTOS tükre (`20260812150000_c3_spine_fields/migration.sql`).
 *
 * A kettőt EGYÜTT kell módosítani; erre teszt is van (`spine.test.ts`).
 */
export const SPINE_PERIOD_PATTERN = /^(0[1-9]|1[0-2])(-(0[1-9]|[12][0-9]|3[01]))?$/;
/** A `spine_number_from` / `spine_number_to` oszlopszélessége (`VarChar(20)`). */
const SPINE_NUMBER_MAX_LENGTH = 20;
/**
 * VÉDŐHÁLÓ: a parser kimenete SOSEM sértheti az adatbázis ellenőrzéseit.
 *
 * A gerincparser legjobb szándékú kinyerés — egy fel nem ismert mező helyes
 * eredménye a `null`. Egy ÉRVÉNYTELEN mező viszont a `folder` sor beszúrását
 * bontja el, tehát nem adathiba lesz belőle, hanem MEGHIÚSULT
 * MAPPAREGISZTRÁCIÓ: a mappa a TC22 Tálcáján ragad, és minden
 * újrapróbálkozás ugyanúgy elbukik.
 *
 * ⚠ EZ A HÁLÓ A `folder` TÁBLA MINDEN GERINC-MEGSZORÍTÁSÁT LEFEDI. Az első
 * változata csak az időszakot nézte, és két órával a kitelepítése után egy
 * `loo 0.` OCR-szöveg a `folder_spine_volume_positive_ck`-n bukott el —
 * ugyanaz a hiba, másik mezőn. A hálót ezért nem mezőnként bővítjük, hanem a
 * megszorítások TELJES listájából vezetjük le:
 *
 *   folder_spine_period_format_ck    → `MM` vagy `MM-DD`
 *   folder_spine_volume_positive_ck  → egész, > 0
 *   folder_spine_year_range_ck       → yearFrom <= yearTo
 *   spine_number_from/to VarChar(20) → hossz
 *
 * Ha a migráció új megszorítást vesz fel a gerincmezőkre, ide is fel kell
 * venni — a `spine.test.ts` „minden mező" tesztje erre emlékeztet.
 *
 * A sértő mezőt `null`-ra ejtjük, a párját is (egy fél tartomány félrevezető
 * lenne). Egy jövőbeli parserhiba így legrosszabb esetben elveszít egy
 * mezőt — azt egy újraelemzés visszahozza —, de nem tarthat vissza egy
 * mappát a Tálcán. Az elmaradt regisztráció emberi munkája nem szerezhető
 * vissza, egy mező igen.
 */
function szurtEredmeny(fields) {
    const eredmeny = { ...fields };
    const idoszakOk = (eredmeny.periodFrom === null || SPINE_PERIOD_PATTERN.test(eredmeny.periodFrom)) &&
        (eredmeny.periodTo === null || SPINE_PERIOD_PATTERN.test(eredmeny.periodTo));
    if (!idoszakOk) {
        eredmeny.periodFrom = null;
        eredmeny.periodTo = null;
    }
    const kotetOk = eredmeny.volume === null || (Number.isInteger(eredmeny.volume) && eredmeny.volume > 0);
    if (!kotetOk)
        eredmeny.volume = null;
    const evOk = eredmeny.yearFrom === null || eredmeny.yearTo === null || eredmeny.yearFrom <= eredmeny.yearTo;
    if (!evOk) {
        eredmeny.yearFrom = null;
        eredmeny.yearTo = null;
    }
    const szamOk = (eredmeny.numberFrom === null || eredmeny.numberFrom.length <= SPINE_NUMBER_MAX_LENGTH) &&
        (eredmeny.numberTo === null || eredmeny.numberTo.length <= SPINE_NUMBER_MAX_LENGTH);
    if (!szamOk) {
        eredmeny.numberFrom = null;
        eredmeny.numberTo = null;
    }
    return eredmeny;
}
function elemez(text, companyAliases, ignorePatterns) {
    // A saját vonalkódunk kiszűrése MINDEN értelmezés előtt.
    const cleaned = text.replace(OWN_BARCODE_PATTERN, ' ');
    const haystack = stripIgnored(normalize(cleaned), ignorePatterns);
    const { yearFrom, yearTo } = parseYears(haystack);
    const { periodFrom, periodTo } = parsePeriod(haystack);
    const { numberFrom, numberTo } = parseNumberRange(haystack);
    return {
        company: matchCompany(haystack, companyAliases) ?? UNCLASSIFIED_COMPANY,
        documentType: matchAlias(haystack, DOCUMENT_TYPE_ALIASES),
        currency: matchAlias(haystack, CURRENCY_ALIASES),
        yearFrom,
        yearTo,
        periodFrom,
        periodTo,
        numberFrom,
        numberTo,
        volume: parseVolume(haystack),
    };
}
/**
 * A fényképen látott, de a mappáéval NEM egyező saját vonalkódok.
 *
 * A címke szándékosan rajta van a gerincfotón, hogy a párosítás
 * visszakereshető legyen. Ez a függvény ezt egy AZONNALI ellenőrzéssé is
 * teszi: ha a felismert szövegben olyan `MAP-#####` szerepel, ami nem a
 * mappa saját vonalkódja, az vagy rossz párosítás, vagy félreolvasás —
 * mindkettő emberi átnézést érdemel.
 *
 * Élő példa: a `MAP-00611` fotóján az Azure AI `MAP-00605`-öt olvasott.
 */
export function foreignBarcodesIn(recognizedText, ownBarcode) {
    if (!recognizedText)
        return [];
    const found = recognizedText.match(OWN_BARCODE_PATTERN) ?? [];
    return [...new Set(found)].filter((code) => code !== ownBarcode);
}
/**
 * Egy felvitt cég-alias normalizálása TÁROLÁS előtt (F1).
 *
 * UGYANAZT a `normalize()`-t használja, amit a parser a gerincszövegre — ez
 * nem kényelmi kérdés: ha az alias „Perspéd" alakban tárolódna, sosem
 * illeszkedne a normalizált szövegre, és a felhasználó nem értené, miért
 * nem működik a frissen felvett cégnév.
 */
export function normalizeAlias(alias) {
    return normalize(alias).trim().replace(/\s+/g, ' ');
}
//# sourceMappingURL=spine.js.map